/**
 * PlaybookPropertyGroup — one property's rules on the playbook page: a collapsible group whose
 * header (56px) shows the property name and its counts, and whose body is the same rules and
 * defaults controls as All properties, with defaults offering off / reword / on / reset. Each
 * group has its own save bar and shows its own 422 (BR-SETTINGS-AGENTS-SAVE).
 */

import { useId, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CatalogItem, PlaybookAgentKind, PlaybookDefaultView } from '@resida/api-types/agent';
import { Button } from '../../ui/button';
import type { usePlaybookDraft } from '../../../hooks/usePlaybookDraft';
import type { PlaybookErrorMap } from '../../../lib/playbook';
import { PlaybookDefaultsList } from './PlaybookDefaultsList';
import { PlaybookRulesEditor } from './PlaybookRulesEditor';

export interface PlaybookScopeCounts {
  rules: number;
  defaultsOff: number;
  defaultsReworded: number;
}

/** "2 rules · 1 default changed" (zero parts omitted); each part has its own test id. */
export function PlaybookCounts({ counts, testIdPrefix }: { counts: PlaybookScopeCounts; testIdPrefix: string }) {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const defaultsChanged = counts.defaultsOff + counts.defaultsReworded;
  const parts: Array<{ key: string; text: string }> = [
    counts.rules > 0 ? { key: 'rules', text: plural(counts.rules, 'rule', 'rules') } : null,
    defaultsChanged > 0
      ? { key: 'defaults-changed', text: plural(defaultsChanged, 'default changed', 'defaults changed') }
      : null,
  ].filter((p): p is { key: string; text: string } => p !== null);
  if (parts.length === 0) {
    return (
      <span className="text-[13px] text-muted-foreground" data-testid={`${testIdPrefix}-counts`}>
        No changes yet
      </span>
    );
  }
  return (
    <span className="text-[13px] text-muted-foreground" data-testid={`${testIdPrefix}-counts`}>
      {parts.map((part, i) => (
        <span key={part.key}>
          {i > 0 && ' · '}
          <span data-testid={`${testIdPrefix}-count-${part.key}`}>{part.text}</span>
        </span>
      ))}
    </span>
  );
}

export interface PlaybookGroupSaveState {
  saving: boolean;
  saveError: string | null;
  errors: PlaybookErrorMap;
  statusMessage: string | null;
  onSave: () => void;
  onDiscard: () => void;
}

export interface PlaybookPropertyGroupProps {
  agentKind: PlaybookAgentKind;
  propertyId: string;
  propertyName: string;
  /** The saved counts the read returned; zeros for a group whose document does not exist yet. */
  counts: PlaybookScopeCounts;
  expanded: boolean;
  onToggle: () => void;
  scope: ReturnType<ReturnType<typeof usePlaybookDraft>['scope']>;
  catalogDefaults: CatalogItem[];
  /** The saved all-properties defaults: what this group inherits. */
  inheritedFrom: PlaybookDefaultView[];
  save: PlaybookGroupSaveState;
}

export function PlaybookPropertyGroup({
  agentKind,
  propertyId,
  propertyName,
  counts,
  expanded,
  onToggle,
  scope,
  catalogDefaults,
  inheritedFrom,
  save,
}: PlaybookPropertyGroupProps) {
  const bodyId = useId();
  const prefix = `playbook-property-${propertyId}`;
  const { draft, changeCount, isDirty } = scope;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  let saveStatus: ReactNode;
  if (isDirty) {
    saveStatus = (
      <span className="mr-auto text-sm font-semibold text-warning" data-testid={`playbook-group-unsaved-${propertyId}`}>
        {changeCount === 1 ? '1 unsaved change' : `${changeCount} unsaved changes`}
      </span>
    );
  } else if (save.statusMessage) {
    saveStatus = (
      <span role="status" className="mr-auto text-sm font-semibold text-success" data-testid={`playbook-group-status-${propertyId}`}>
        {save.statusMessage}
      </span>
    );
  } else {
    saveStatus = (
      <span className="mr-auto text-sm text-muted-foreground" data-testid={`playbook-group-clean-${propertyId}`}>
        All changes saved
      </span>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`playbook-group-${propertyId}`}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={onToggle}
        data-testid={`playbook-group-toggle-${propertyId}`}
        className="flex min-h-[56px] w-full items-center gap-3 rounded-xl px-5 text-left text-foreground hover:bg-accent/50"
      >
        <Chevron className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 break-words text-[15px] font-semibold" data-testid={`playbook-group-name-${propertyId}`}>
          {propertyName}
        </span>
        <PlaybookCounts counts={counts} testIdPrefix={`playbook-group-${propertyId}`} />
        {isDirty && (
          <span className="h-2 w-2 shrink-0 rounded-full bg-warning" aria-label="Unsaved changes" role="img" />
        )}
      </button>

      {expanded && (
        <div id={bodyId} className="flex flex-col gap-6 px-5 pb-5" data-testid={`playbook-group-body-${propertyId}`}>
          <PlaybookRulesEditor
            agentKind={agentKind}
            heading={`Rules for ${propertyName}`}
            idPrefix={prefix}
            rules={draft.rules}
            errors={save.errors.rules}
            listError={save.errors.general.join(' ') || undefined}
            onAdd={scope.addRule}
            onEdit={scope.editRule}
            onRemove={scope.removeRule}
            onMoveUp={scope.moveRuleUp}
          />
          <PlaybookDefaultsList
            idPrefix={prefix}
            inheritedFrom={inheritedFrom}
            catalogDefaults={catalogDefaults}
            customizations={draft.defaults}
            errors={save.errors.defaults}
            onToggle={scope.toggleDefault}
            onReword={scope.rewordDefault}
            onReset={scope.resetDefault}
            onRemoveRetired={scope.removeRetired}
          />

          {save.saveError && (
            <div
              role="alert"
              data-testid={`playbook-group-save-error-${propertyId}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
            >
              <span className="text-destructive">{save.saveError}</span>
              <Button type="button" variant="outline" onClick={save.onSave} data-testid={`playbook-group-save-retry-${propertyId}`}>
                Retry
              </Button>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3" data-testid={`playbook-group-save-bar-${propertyId}`}>
            {saveStatus}
            {isDirty && (
              <Button type="button" variant="ghost" onClick={save.onDiscard} disabled={save.saving} data-testid={`playbook-group-discard-${propertyId}`}>
                Discard
              </Button>
            )}
            <Button
              type="button"
              onClick={save.onSave}
              disabled={!isDirty || save.saving}
              aria-label={`Save changes for ${propertyName}`}
              data-testid={`playbook-group-save-${propertyId}`}
            >
              {save.saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaybookPropertyGroup;
